import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../../../store/userStore';
import { paymentApi, userApi } from '../../../services/api';
import { PaymentPlan, PaymentRequest } from '../../../types';

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, setMembershipPasses } = useUserStore();
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState<any>(null);

  // 이용권 요금제 로드
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const plansData = await paymentApi.getPlans();
        setPlans(plansData);

        // 인기 요금제를 기본 선택
        const popularPlan = plansData.find((plan) => plan.popular);
        if (popularPlan) {
          setSelectedPlan(popularPlan);
        }
      } catch (error) {
        console.error('Failed to load payment plans:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlans();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원';
  };

  const formatMinutes = (minutes: number) => {
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return `${days}일`;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return `${hours}시간`;
    } else {
      return `${minutes}분`;
    }
  };

  const handlePayment = async () => {
    if (!currentUser || !selectedPlan) {
      alert('로그인이 필요합니다.');
      return;
    }

    setIsProcessing(true);

    try {
      const paymentData: PaymentRequest = {
        pass_type: selectedPlan.id,
        amount: selectedPlan.price,
        payment_method: paymentMethod,
      };

      const result = await paymentApi.processPayment(
        currentUser.id,
        paymentData
      );

      setPaymentResult(result);
      setShowResult(true);

      if (result.success && result.pass) {
        // 성공 시 이용권 목록 업데이트
        const updatedPasses = await userApi.getMembershipPasses(currentUser.id.toString());
        setMembershipPasses(updatedPasses);

        setTimeout(() => {
          setShowResult(false);
          navigate('/dashboard/profile');
        }, 3000);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      setPaymentResult({
        success: false,
        error: '결제 처리 중 오류가 발생했습니다.',
      });
      setShowResult(true);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          {paymentResult?.success ? (
            <>
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                결제 완료!
              </h2>
              <p className="text-gray-600 mb-4">
                이용권이 성공적으로 구매되었습니다.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  <strong>{selectedPlan?.name}</strong> 구매 완료
                </p>
                <p className="text-sm text-green-600">
                  {formatPrice(selectedPlan?.price || 0)}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                3초 후 프로필 페이지로 이동합니다...
              </p>
            </>
          ) : (
            <>
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                결제 실패
              </h2>
              <p className="text-gray-600 mb-4">
                {paymentResult?.error || '결제 처리 중 오류가 발생했습니다.'}
              </p>
              <button
                onClick={() => setShowResult(false)}
                className="btn btn-primary"
              >
                다시 시도
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">이용권 구매</h1>
          <p className="text-gray-600">
            원하는 이용권을 선택하고 결제를 진행하세요.
          </p>
        </div>

        {/* 이용권 선택 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">이용권 선택</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`
                  relative border-2 rounded-lg p-6 cursor-pointer transition-all
                  ${
                    selectedPlan?.id === plan.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }
                  ${plan.popular ? 'ring-2 ring-primary-200' : ''}
                `}
                onClick={() => setSelectedPlan(plan)}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      인기
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {plan.description}
                  </p>

                  <div className="mb-4">
                    <div className="text-3xl font-bold text-primary-600">
                      {formatPrice(plan.price)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatMinutes(plan.minutes)}
                    </div>
                  </div>

                  {selectedPlan?.id === plan.id && (
                    <CheckCircleIcon className="h-6 w-6 text-primary-500 mx-auto" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 결제 수단 선택 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            결제 수단 선택
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`
                border-2 rounded-lg p-4 cursor-pointer transition-all
                ${
                  paymentMethod === 'card'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }
              `}
              onClick={() => setPaymentMethod('card')}
            >
              <div className="flex items-center">
                <CreditCardIcon className="h-6 w-6 text-gray-600 mr-3" />
                <span className="font-medium">신용카드</span>
                {paymentMethod === 'card' && (
                  <CheckCircleIcon className="h-5 w-5 text-primary-500 ml-auto" />
                )}
              </div>
            </div>

            <div
              className={`
                border-2 rounded-lg p-4 cursor-pointer transition-all
                ${
                  paymentMethod === 'cash'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }
              `}
              onClick={() => setPaymentMethod('cash')}
            >
              <div className="flex items-center">
                <BanknotesIcon className="h-6 w-6 text-gray-600 mr-3" />
                <span className="font-medium">현금</span>
                {paymentMethod === 'cash' && (
                  <CheckCircleIcon className="h-5 w-5 text-primary-500 ml-auto" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 결제 요약 및 진행 */}
        {selectedPlan && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">결제 요약</h2>

            <div className="border-t border-b py-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">선택 이용권</span>
                <span className="font-medium">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">이용 시간</span>
                <span className="font-medium">
                  {formatMinutes(selectedPlan.minutes)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">결제 수단</span>
                <span className="font-medium">
                  {paymentMethod === 'card' ? '신용카드' : '현금'}
                </span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold">
                <span>총 결제 금액</span>
                <span className="text-primary-600">
                  {formatPrice(selectedPlan.price)}
                </span>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => navigate('/dashboard/profile')}
                className="btn btn-secondary flex-1"
              >
                취소
              </button>
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                    결제 처리 중...
                  </div>
                ) : (
                  '결제하기'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
